import type { ProviderFeatureName, UserRoleName } from 'src/generated'

// ----------------------------------------------------------------------
// The question every restricted screen asks, as one function.
//
// `ProtectedRoute` is the half that refuses a typed or bookmarked address, and
// it used to hold this logic inline. A button or a menu item that leads to a
// restricted screen has to ask exactly the same thing, or it offers a press
// that lands on the refusal: the vehicle page's Edit button did that for every
// provider admin whose plan did not include editing. So the gate's rule lives
// here, and a link into a gated screen asks it with the same arguments the
// route is given.
// ----------------------------------------------------------------------

/** What a gated screen requires. Spread onto `ProtectedRoute` as props. */
export interface RouteAccessRule {
  requiredFeatureFlag?: ProviderFeatureName
  requiredRole?: readonly UserRoleName[]
}

export interface ProviderFeatureFlag {
  name: ProviderFeatureName
  enabled: boolean
}

/**
 * Whether a viewer with `role` on a provider with `features` may open a screen
 * gated by `rule`.
 *
 * Beast staff are admitted regardless of flag and role, which is the override
 * the console has always had. Everyone else needs both: a role on the list and,
 * when the rule names one, the flag switched on. A rule with no roles admits
 * nobody but staff, so a route added without them fails closed.
 */
export const routeAccessAllowed = (
  { requiredFeatureFlag, requiredRole }: RouteAccessRule,
  role: UserRoleName | null | undefined,
  features: readonly ProviderFeatureFlag[],
): boolean => {
  if (role === 'superAdmin') {
    return true
  }

  const featureEnabled =
    !requiredFeatureFlag ||
    features.some(
      (feature) => feature.name === requiredFeatureFlag && feature.enabled,
    )

  const hasRole = role != null && (requiredRole?.includes(role) ?? false)

  return featureEnabled && hasRole
}
